// Rendu Chart.js (ADR-0008) — v2 harvest ancien site : vraie pyramide des âges (miroir),
// échelles intelligentes (les évolutions ne s'écrasent plus sur un axe qui part de zéro),
// tooltips explicites (valeurs formatées fr-FR, parts en % pour les camemberts).
import { useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import Chart from 'chart.js/auto'
import type { ChartConfiguration, ChartType, LegendItem } from 'chart.js'
import { useAuth } from '../AuthContext'
import { API } from '../obs/types'
import { serieNonIndexable } from './base100'
import { moyenneMobile3Ans } from './moyenneMobile'
import { placerEtiquettesCourbes, type PointCourbe } from './etiquettesCourbes'
import { BasculeVue, estRoutePrint, useVueDonnees } from '../obs/tableau/BasculeVue'
import { TableauSpec } from '../obs/tableau/TableauSpec'
import { estProportion, fmtFr, fmtPart, libelleCategorie } from '../obs/tableau/format'
import { basculer, cibleLegende } from '../obs/tableau/legende'
import { formeEchelles, totalPourPart } from '../obs/tableau/echelles'
import { etatStructure, lignesCentre, specStructure, texteLegende } from '../obs/tableau/structure'
import { BasculeStructure, ChoixStrate } from '../obs/tableau/BasculeStructure'
import { useFormeRepartition } from '../obs/tableau/formeRepartition'
import { NomCommuneCtx } from '../obs/nomCommune'
import { rappelsDernierRepere } from './axeTemporel'
import ComparerA, { type PuceComparee } from '../obs/ComparerA'
import { NOM_PRODUIT } from '../constants'
import { couleurSerie } from './couleursSeries'
import { basAxeAjuste, useThemeGraphiques } from './themeGraphiques'
import { axeTerritoires, replierComparaisons } from '../obs/repliComparaisons'
import { deposerGraphiqueComplet } from '../obs/partage/rejeu'

export interface ChartSpec {
  type: 'bar' | 'line' | 'doughnut' | 'pyramid'
  labels: (string | number)[]
  // abs/absLabel : série des valeurs ABSOLUES alignée sur data (ex. nombre de faits),
  // affichée dans l'infobulle à côté du taux — demande Marc 16/07 soir.
  // notes : un TEXTE par point, aligné sur data, rendu en lignes supplémentaires dans
  // l'infobulle — recette Marc 02/08 sur les catastrophes naturelles (« quand on clique
  // sur l'année, une infobulle avec le nom de la catastrophe naturelle »). Le point
  // porte un chiffre, la note dit CE QUE c'est.
  // `unit` d'une SÉRIE : sur un graphique à panneaux (`panneaux`), chaque série a la sienne
  // (feux, hectares) ; ailleurs, l'unité est celle du graphique (`unit`).
  datasets: { label: string; data: (number | null)[]; abs?: (number | null)[]
              absLabel?: string; notes?: (string | null)[]; unit?: string }[]
  // `zerosMuets` : ne pas écrire l'étiquette « 0 » sur les points nuls. Réservé aux séries
  // à grille continue où les zéros sont MAJORITAIRES et attendus (arrêtés catnat année par
  // année). Opt-in : partout ailleurs, un zéro affiché est une mesure, et le taire le
  // rendrait indistinguable d'une absence.
  zerosMuets?: boolean
  // Unité affichée sur l'axe Y et dans les tooltips (ex. '‰') — lisibilité SSMSI 16/07 :
  // « ce n'est pas vraiment expliqué que c'est du pour mille » (Marc).
  unit?: string
  // Pyramide uniquement (demande Marc 31/07 soir) : le détail âge par âge embarqué par le
  // builder (switch quinquennal ↔ âge), et le drapeau « comparable » qui ouvre le sélecteur
  // de référent (endpoint /pyramide-comparee, niveau FREE).
  detail?: { labels: (string | number)[]; datasets: { label: string; data: (number | null)[] }[]; note?: string }
  comparable?: boolean
  // Barres empilées 100 % (dem-4, refonte 03/08) : chaque barre totalise 100 % de SON
  // échelle — l'axe des valeurs est borné à 100 et les étiquettes-valeurs se taisent
  // (7 segments × 4 barres annotés seraient du bruit ; l'infobulle reste).
  empile?: boolean
  // Barres horizontales hors pyramide (les libellés d'échelles sont longs).
  horizontal?: boolean
  // dem-1 (#112) : la courbe longue porte le curseur d'année de départ et le comparateur
  // multi-communes (base 100 à l'année de départ en mode comparaison).
  histo_comparable?: boolean
  // L-G (05/08) : le comparateur devient générique — `histo_graphe` nomme la série côté
  // endpoint (absent = population, contrat dem-1 inchangé) ; `histo_unite` remplace le
  // libellé « Effectifs » du toggle quand la série n'est pas un décompte de personnes.
  histo_graphe?: string
  histo_unite?: string
  // Opt-in des séries annuelles (log-9, retour Olivier du 15/10).
  moyenne_mobile?: boolean
  // L'année en cours, incomplète (servie par le builder) : aucune moyenne mobile ne la prend.
  annee_partielle?: number
  // Empilés 100 % (#112, élargi T2 05/08 aux graphes du logement) : « + communes »
  // ajoute une barre par témoin choisi — la valeur est le nom du graphe côté endpoint.
  barres_comparables?: string
  // T2 (05/08) : palette par CATÉGORIE — 'dpe' = les couleurs officielles de l'étiquette
  // énergie (A vert foncé → G rouge), demande Marc. La lettre reste écrite sur chaque
  // segment : la couleur seule exclurait les daltoniens.
  palette?: 'dpe'
  // T2 (05/08) : barres cliquables — un clic sur une barre émet l'événement global
  // `obs:annee` avec le libellé de la catégorie (la carte SITADEL l'écoute pour ouvrir
  // le détail de l'année cliquée).
  clic_annees?: boolean
  // Graphique à PANNEAUX (réunion des fondateurs du 14/10 : les incendies de forêt en un seul
  // graphique, le nombre de feux et la surface parcourue). Une série par panneau, les panneaux
  // empilés l'un SOUS l'autre dans la même toile et alignés sur les mêmes catégories (années),
  // chacun avec SON axe des valeurs et SON unité (`datasets[i].unit`). Ce n'est pas un double
  // axe — deux échelles sur une même aire de tracé, qui font lire une corrélation que le choix
  // des échelles fabrique — : chaque aire n'a qu'une échelle. Barres verticales seulement.
  panneaux?: boolean
  // P24 (D4, démo du 09/10) : le TOTAL déclaré par le producteur, quand des décomptes
  // forment un tout (la somme des barres est le total de la commune). Seul ce champ autorise
  // la vue « Structure » sur des valeurs absolues — voir `obs/tableau/structure`.
  total?: number
}

// `libelle` : le NOM réel du périmètre, servi par le backend avec la donnée (Marc 06/08 —
// « CA Communauté Paris-Saclay » plutôt que « Son intercommunalité »). Optionnel : les
// réponses antérieures au déploiement ou un référentiel troué retombent sur le générique.
type ProfilPyr = { tranches: string[]; hommes_pct: number[]; femmes_pct: number[]
  libelle?: string }
interface PyramideRef {
  granularite: string
  commune: ProfilPyr
  epci: ProfilPyr
  departement: ProfilPyr
  strate: ProfilPyr
  france: ProfilPyr
  commune_cible?: ProfilPyr
  commune_cible_code?: string
}
// Recette Marc 03/08 (#112) : la strate sort des cibles — doctrine « échelles emboîtées
// + communes choisies ». Les communes (témoins proposés + jetons) s'ajoutent dynamiquement
// au sélecteur, id `c:INSEE`.
const CIBLES_PYR = [
  { id: 'aucune', label: 'Aucune' },
  { id: 'epci', label: 'Son intercommunalité' },
  { id: 'departement', label: 'Son département' },
  { id: 'france', label: 'France métropolitaine' },
] as const
interface CommuneDispo { code: string; nom: string; origine: string }
// « (proposée) » ne disait pas QUI propose (CDC du 09/10, lot B) : un pair proposé par
// le produit, à distinguer des jetons que le compte a choisis lui-même.
const libelleCommune = (c: CommuneDispo) =>
  c.origine === 'propose' ? `${c.nom} · proposée par ${NOM_PRODUIT}` : c.nom
const HISTO_MAX = 4
const BARRES_MAX = 3
// P1 (démo de Besançon du 09/10, D3 : « quand tu ajoutes à comparer… il ne l'a pas affiché »).
// Trois silences expliquaient ce « rien ne se passe » : le plafond refusait sans un mot, un
// doublon aussi, et l'appel au serveur — plusieurs secondes sur des témoins fraîchement
// confirmés (D2) — ne montrait rien pendant l'attente. Désormais le plafond se lit à côté
// du sélecteur (désactivé), les cibles déjà ajoutées quittent la liste, l'ajout s'annonce
// pendant qu'il charge, une réponse sans valeur se dit, et une erreur dit son statut.
const SANS_VALEUR = 'aucune valeur disponible pour ce graphique'
const messagePlafond = (n: number) =>
  `${n} comparaisons au plus — retirez-en une pour en ajouter une autre`
const aUneValeur = (v: unknown) =>
  Array.isArray(v) && v.some(x => x !== null && x !== undefined)
// « 403 » dit « pas un témoin de l'espace », « 500 » dit « serveur », « réseau » dit le reste :
// un seul mot « indisponible » cachait ces trois causes au lecteur comme au support.
const messageErreur = (e: unknown) => {
  const statut = (e as { response?: { status?: number } } | null)?.response?.status
  return statut ? `comparaison indisponible (erreur ${statut})` : 'comparaison indisponible (réseau)'
}
// Apparence EXPLICITE des sélecteurs « Comparer à » (Marc, 11/10 : « le "+ ajouter" n'a
// toujours pas de contour, il ne paraît pas clair d'utilisation ») : Tailwind retire le style
// natif du `<select>`, qui sortait sans bordure ni fond — un contrôle qu'on ne voit pas est un
// contrôle qui n'existe pas. Même traitement que le sélecteur d'échelle de la synthèse
// (`SyntheseView`), lot B du CDC du 09/10 (« Comparer à » dans un vrai cadre).
const STYLE_SELECT_COMPARER = {
  fontSize: 13, padding: '3px 8px', borderRadius: 6,
  border: '1px solid var(--c-border, #d1d5db)',
  // Fond BLANC : le cadre « Comparer à » (lot B) est lui-même sur `--c-bg` — un sélecteur
  // du même gris s'y fondrait, contour ou pas.
  background: 'white', color: 'inherit',
} as const
// Échelles agrégées de la courbe longue (vague 3, #112) — servies sans compte, comme les
// comparaisons de KPI : un agrégat de périmètre n'est la donnée d'aucune autre commune.
const HIST_ECHELLES = [
  { id: 'e:epci', label: 'Intercommunalité' },
  { id: 'e:departement', label: 'Département' },
  { id: 'e:region', label: 'Région' },
  { id: 'e:france', label: 'France métropolitaine' },
] as const

// Virgule décimale FR partout (revue 16/07). Le formateur vit désormais dans
// `obs/tableau/format` : le tableau qui double le graphique doit écrire ses nombres
// EXACTEMENT comme le tracé, et deux copies auraient fini par diverger.
const fmt = fmtFr
const LABEL_INK = '#334155'
const MARGE_ETIQUETTES_COURBES = 14
// P24 : en deçà de cette largeur de toile, la légende de l'anneau passe sous lui — à droite,
// sur 375 px, elle écraserait l'anneau en un anneau de quelques pixels.
const LARGEUR_LEGENDE_DROITE = 480
const legendeADroite = (toile: HTMLCanvasElement | null) =>
  (toile?.parentElement?.clientWidth ?? 0) >= LARGEUR_LEGENDE_DROITE
const LABEL_ND = '#94A3B8'
/** La hauteur de la boîte d'un graphique en barres horizontales : une rangée de 44 px par barre. */
const hauteurHorizontale = (nbBarres: number) => 90 + 44 * nbBarres
/** Aucune série masquée (jamais modifié) : celles d'une tuile repliée par territoires. */
const AUCUNE_SERIE: Set<string> = new Set()

// ── Étiquettes de valeur SUR le graphique (issue #33) ────────────────────────────────────
// Jusqu'ici il fallait SURVOLER pour lire un chiffre. Sur un export PDF, une capture d'écran
// ou une impression, l'élu ne le lisait donc jamais — or notre promesse est de publier des
// données certifiées, et un chiffre qu'on ne peut lire qu'en interagissant n'est pas publié.
//
// Plugin maison plutôt que `chartjs-plugin-datalabels` : une dépendance de moins à suivre, et
// surtout le contrôle de l'anti-chevauchement, qui est TOUT le sujet ici (la pyramide des âges
// a 20 tranches, les séries longues autant de points). La règle : on n'écrit une étiquette que
// si son rectangle ne touche aucune de celles déjà posées. Mieux vaut une étiquette sur deux
// que deux chiffres illisibles l'un sur l'autre.
// On DÉCLARE l'option auprès de Chart.js au lieu de la caster : un cast tairait aussi une
// faute de frappe sur le nom, et le plugin se contenterait alors de ne rien dessiner.
declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    obsEtiquettesValeur?: { unit?: string; kind?: ChartSpec['type']; off?: boolean
                            zerosMuets?: boolean
                            // D-8 (#260) : une barre HORIZONTALE simple pose son étiquette
                            // au BOUT de la barre, pas au-dessus — comme la pyramide, qui
                            // est elle aussi horizontale. `partTotal`, quand il est fourni,
                            // ajoute la part à côté de la valeur (« 1 234 (35 %) »).
                            horizontal?: boolean; partTotal?: number
                            // Thème des graphiques (themeGraphiques.ts) ; absents, encres et
                            // police du site.
                            encre?: string; encreND?: string; police?: string
                            // graphique à panneaux : chaque série se pose dans SON panneau
                            // (N/D à son pied, rabat sous son sommet), avec SON unité
                            panneaux?: boolean }
    obsAxeTronque?: { encre?: string; horizontal?: boolean }
    // P24, suite (Marc, 13/10) : le nom du territoire au centre de l'anneau « Structure ».
    obsCentreAnneau?: { texte?: string; encre?: string; police?: string }
  }
}

type Boite = { x1: number; y1: number; x2: number; y2: number }

function libre(boites: Boite[], b: Boite): boolean {
  // Marges asymétriques : 3 px à l'horizontale (deux nombres collés se lisent mal), 1 px à
  // la verticale (sur une pyramide de 20 rangées, 2 px suffisaient à en sacrifier la moitié).
  return !boites.some(o => b.x1 < o.x2 + 3 && b.x2 + 3 > o.x1 && b.y1 < o.y2 + 1 && b.y2 + 1 > o.y1)
}

const placementsCourbes = new WeakMap<Chart, {
  geometrie: string; poses: ReturnType<typeof placerEtiquettesCourbes>
}>()

const etiquettesDeValeur = {
  id: 'obsEtiquettesValeur',
  afterDatasetsDraw(chart: Chart) {
    const o = chart.options.plugins?.obsEtiquettesValeur
    if (!o || o.off) return
    const { ctx, chartArea } = chart
    const pyramide = o.kind === 'pyramid'
    const anneau = o.kind === 'doughnut'
    const boites: Boite[] = []

    ctx.save()
    // Un cran plus petit sur la pyramide : 20 tranches sur ~340 px de haut, et l'écart entre
    // deux rangées ne laisse pas la place à du 11 px sans que l'anti-collision n'en sacrifie
    // une sur deux. Vérifié à l'écran : en 10 px les 20 valeurs tiennent.
    ctx.font = `600 ${pyramide ? 10 : 11}px ${o.police ?? 'system-ui, -apple-system, "Segoe UI", sans-serif'}`
    ctx.textBaseline = 'middle'

    if (o.kind === 'line') {
      // Toutes les séries partagent les obstacles, y compris celles dont les valeurs
      // seront cachées. Les contrôles Bézier sont ceux du tracé Chart.js déjà dessiné.
      const textes: string[][] = []
      const series = chart.data.datasets.map((ds, di) => {
        const meta = chart.getDatasetMeta(di)
        const unite = (ds as { unit?: string }).unit ?? o.unit
        textes[di] = []
        return { epaisseur: typeof ds.borderWidth === 'number' ? ds.borderWidth : 2.5,
          points: !chart.isDatasetVisible(di) ? [] : meta.data.map((el, i): PointCourbe | null => {
            const valeur = ds.data[i]
            const p = el as typeof el & { skip?: boolean; options?: { radius?: number }
              cp1x?: number; cp1y?: number; cp2x?: number; cp2y?: number }
            if (typeof valeur !== 'number' || !Number.isFinite(valeur) || p.skip
                || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return null
            const texte = `${fmt(valeur)}${unite ? ` ${unite}` : ''}`
            textes[di][i] = texte
            const mesure = ctx.measureText(texte)
            // Bornes prudentes, même quand le contexte SVG ne fournit que la largeur.
            const hauteur = Math.max(14, 2 * Math.max(mesure.actualBoundingBoxAscent || 0, mesure.actualBoundingBoxDescent || 0))
            return { x: p.x, y: p.y, valeur, largeur: Math.ceil(mesure.width) + 2, hauteur,
              rayon: p.options?.radius ?? 3, etiqueter: !(o.zerosMuets && valeur === 0),
              cp1x: p.cp1x, cp1y: p.cp1y, cp2x: p.cp2x, cp2y: p.cp2y }
          }) }
      })
      ctx.textAlign = 'left'
      // La marge supérieure de la toile réserve une rangée aux pics, même quand
      // l'arrondi de l'axe laisse peu d'espace entre le maximum et le bord du tracé.
      const cadre = { ...chartArea, top: Math.max(0, chartArea.top - MARGE_ETIQUETTES_COURBES) }
      // Au survol, Chart.js redessine sans déplacer les courbes. On garde le dernier
      // placement par graphique ; points, contrôles, dimensions et visibilité l'invalident.
      const geometrie = JSON.stringify([series, cadre])
      let placement = placementsCourbes.get(chart)
      if (placement?.geometrie !== geometrie) {
        placement = { geometrie, poses: placerEtiquettesCourbes(series, cadre) }
        placementsCourbes.set(chart, placement)
      }
      for (const p of placement.poses) {
        const ds = chart.data.datasets[p.serie]
        ctx.fillStyle = typeof ds.borderColor === 'string' ? ds.borderColor : (o.encre ?? LABEL_INK)
        ctx.fillText(textes[p.serie][p.point], p.x1 + 1, (p.y1 + p.y2) / 2)
      }
      ctx.restore()
      return
    }

    chart.data.datasets.forEach((ds, di) => {
      if (chart.getDatasetMeta(di).hidden) return
      const meta = chart.getDatasetMeta(di)
      // Graphique à panneaux : le cadre d'une série est celui de SON axe des valeurs, pas toute
      // la toile — sinon le N/D du panneau du haut se poserait au pied de celui du bas.
      const cadre = o.panneaux && meta.yScale
        ? { top: meta.yScale.top, bottom: meta.yScale.bottom } : chartArea
      const unite = (ds as { unit?: string }).unit ?? o.unit
      meta.data.forEach((el, i) => {
        const brut = ds.data[i] as number | null
        const total = anneau
          ? (ds.data as (number | null)[]).reduce<number>((s, v) => s + (v ?? 0), 0)
          : 0

        // N/D : sur un histogramme, une barre absente est INVISIBLE — rien ne la distingue
        // d'un zéro, et zéro est une mesure alors que N/D est une lacune. On l'écrit donc.
        // Sur une courbe, la rupture de trait le dit déjà : inutile de surcharger.
        if (brut === null || brut === undefined) {
          if (o.kind !== 'bar' || pyramide) return
          const x = el.x
          const y = cadre.bottom - 9
          const l = ctx.measureText('N/D').width
          const b = { x1: x - l / 2, y1: y - 6, x2: x + l / 2, y2: y + 6 }
          if (!libre(boites, b)) return
          boites.push(b)
          ctx.fillStyle = o.encreND ?? LABEL_ND
          ctx.textAlign = 'center'
          ctx.fillText('N/D', x, y)
          return
        }

        // ZÉROS MUETS quand la série le demande (recette Marc 02/08, sur les arrêtés de
        // catastrophe naturelle : « j'aurais juste retiré les valeurs à 0, elles sont
        // redondantes »). Sur une grille de 45 années dont 31 sans arrêté, trente et un
        // « 0 » ne disent rien qu'une barre à hauteur nulle ne dise déjà — ils masquent
        // les années qui comptent. Le comportement reste OPT-IN : ailleurs, un zéro
        // affiché est une mesure qu'il faut pouvoir distinguer d'une absence.
        if (o.zerosMuets && brut === 0) return

        // Anneau : la part en % dit ce que la valeur brute ne dit pas (« 12 sur combien ? »).
        // Sous 7 % l'arc est trop étroit pour accueillir un texte lisible — le tooltip reste là.
        let texte: string
        if (anneau) {
          const part = total ? (100 * brut) / total : 0
          if (part < 7) return
          texte = `${part.toFixed(0)} %`
        } else {
          texte = `${fmt(pyramide ? Math.abs(brut) : brut)}${unite ? ` ${unite}` : ''}`
          // D-8 : « part et valeur au bout de chaque barre ». Le total n'est fourni que
          // lorsqu'il a un sens (série unique, valeurs absolues — voir `totalPourPart`) ;
          // sinon la barre porte sa valeur seule, ce qui est toujours vrai.
          if (o.partTotal) texte += ` (${fmtPart(brut, o.partTotal)} %)`
        }

        const largeur = ctx.measureText(texte).width
        let x = el.x
        let y = el.y
        let align: CanvasTextAlign = 'center'

        if (pyramide) {
          // Axe horizontal : hommes en négatif à gauche, femmes à droite. L'étiquette se pose
          // JUSTE À CÔTÉ du bout de barre, à l'extérieur — dedans, elle passerait sur un
          // aplat de couleur dont on ne maîtrise pas le contraste.
          const versGauche = brut < 0
          x = el.x + (versGauche ? -5 : 5)
          align = versGauche ? 'right' : 'left'
        } else if (anneau) {
          // Centre de l'arc : on récupère le point milieu que Chart.js expose sur l'élément.
          const p = (el as unknown as { tooltipPosition: () => { x: number; y: number } })
            .tooltipPosition()
          x = p.x
          y = p.y
        }
        let dansLaBarre = false
        if (o.horizontal && !pyramide && !anneau) {
          // D-8 (#260) : barre horizontale — l'étiquette se pose au BOUT de la barre,
          // centrée sur sa hauteur, comme le fait déjà la pyramide (qui est une barre
          // horizontale). Posée au-dessus, comme sur un histogramme vertical, elle
          // atterrissait entre deux rangées et chevauchait le bout de barre.
          // `el.x` est l'extrémité de la barre, `el.y` son centre vertical.
          x = el.x + 5
          align = 'left'
          // Débordement à droite : plutôt que d'abandonner l'étiquette (le lecteur perdrait
          // justement la plus grande valeur du graphique), on la rentre DANS la barre, en
          // blanc sur l'aplat — même remède que sur un histogramme comprimé.
          if (x + largeur > chartArea.right - 2) {
            x = el.x - 5
            align = 'right'
            dansLaBarre = true
          }
        } else if (!pyramide && !anneau) {
          y = el.y - 11              // au-dessus de la barre ou du point
          if (y < cadre.top + 8) {
            // Rabattue SOUS le sommet — donc DANS la barre pour un histogramme. Constaté
            // sur le PDF de Palaiseau : « 36 067 » en encre sombre sur l'aplat bleu, à
            // peine lisible. La mise en page print compresse la hauteur, le grace de
            // l'échelle ne suffit plus. En blanc sur l'aplat, le chiffre se lit.
            y = el.y + 13
            dansLaBarre = o.kind === 'bar'
          }
        }

        let x1 = align === 'center' ? x - largeur / 2 : align === 'right' ? x - largeur : x

        // Barres et pyramides : ne pas recadrer vers l'intérieur, où le texte
        // passerait sur un aplat. Les courbes ont leur propre placement ci-dessus.
        if (x1 < chartArea.left - 2 || x1 + largeur > chartArea.right + 2) return

        const demi = pyramide ? 5 : 6
        const b = { x1, y1: y - demi, x2: x1 + largeur, y2: y + demi }
        if (!libre(boites, b)) return
        boites.push(b)

        ctx.fillStyle = (anneau || dansLaBarre) ? '#FFFFFF'
          : (o.encre ?? LABEL_INK)
        ctx.textAlign = 'left'          // on dessine depuis x1 recadré, pas depuis l'ancrage
        ctx.fillText(texte, x1, y)
      })
    })
    ctx.restore()
  },
}
// L'axe des valeurs ne part pas de zéro (règle d'axes ajustés, maquettes seulement) : deux
// traits obliques à sa base le disent, comme sur un graphique imprimé. Sans eux, une barre
// deux fois plus haute qu'une autre se lirait comme une valeur double.
const marqueAxeTronque = {
  id: 'obsAxeTronque',
  afterDraw(chart: Chart) {
    const o = chart.options.plugins?.obsAxeTronque
    if (!o) return
    const { ctx, chartArea } = chart
    const x = o.horizontal ? chartArea.left + 4 : chartArea.left
    const y = o.horizontal ? chartArea.bottom : chartArea.bottom - 4
    ctx.save()
    ctx.strokeStyle = o.encre ?? LABEL_INK
    ctx.lineWidth = 1.2
    for (const d of [-3, 3]) {
      ctx.beginPath()
      if (o.horizontal) { ctx.moveTo(x + d - 3, y + 4); ctx.lineTo(x + d + 3, y - 4) }
      else { ctx.moveTo(x - 4, y + d + 3); ctx.lineTo(x + 4, y + d - 3) }
      ctx.stroke()
    }
    ctx.restore()
  },
}
// P24, suite (Marc, 13/10) : l'anneau « Structure » montre UN territoire — son nom s'écrit au
// centre, dans le trou de l'anneau. Dans la toile et non en HTML par-dessus : le centre suit
// l'anneau quand la légende passe à droite ou dessous, et l'image partagée (PNG, SVG — le
// rejeu reprend les greffons) dit de quel territoire elle est la structure.
const centreAnneau = {
  id: 'obsCentreAnneau',
  afterDatasetsDraw(chart: Chart) {
    const o = chart.options.plugins?.obsCentreAnneau
    if (!o?.texte) return
    const arc = chart.getDatasetMeta(0)?.data?.[0] as unknown as
      { getProps?: (p: string[], final?: boolean) => Record<string, number> } | undefined
    const p = arc?.getProps?.(['x', 'y', 'innerRadius'], true)
    // Sous ~30 px de rayon, le trou ne loge pas un nom lisible : l'infobulle et le bouton du
    // territoire le disent déjà.
    if (!p || !(p.innerRadius > 30)) return
    const { ctx } = chart
    const taille = p.innerRadius >= 55 ? 13 : 11
    ctx.save()
    ctx.font = `600 ${taille}px ${o.police ?? 'system-ui, -apple-system, "Segoe UI", sans-serif'}`
    ctx.fillStyle = o.encre ?? LABEL_INK
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const lignes = lignesCentre(o.texte, p.innerRadius * 1.6, t => ctx.measureText(t).width)
    const pas = taille * 1.25
    lignes.forEach((l, i) => ctx.fillText(l, p.x, p.y + (i - (lignes.length - 1) / 2) * pas))
    ctx.restore()
  },
}
// Route print (PDF Chromium headless) : rendu DÉTERMINISTE exigé — l'animation Chart.js
// (~1 s) pouvait être figée à mi-course par la capture --print-to-pdf (B2, réunion 15/07 :
// pyramide plafonnée ~500 au lieu de ~2 500). La détection vit désormais dans
// `obs/tableau/BasculeVue` : les tuiles en ont besoin aussi, et deux copies du même test
// de route auraient dérivé (au premier jet, les tuiles sortaient AVEC leur bascule dans
// le PDF, faute de partager cette condition).
const isPrint = estRoutePrint()

// ── L'alternative textuelle du graphique a déménagé (C1, plan 12/09) ────────────────────
// Elle vivait ici, repliée sous chaque canvas derrière « Voir les données du graphique »
// (RGAA 1.1 / 1.3, passage du 06/08). Elle est désormais `obs/tableau/TableauSpec`, appelée
// par la bascule « Graphique | Tableau » : même construction, même source (le spec EFFECTIF,
// celui que Chart.js reçoit), mais une vue de plein droit au lieu d'un dépliant qu'il
// fallait deviner. Le besoin d'accessibilité est servi à l'identique — mieux, même : le
// tableau est maintenant annoncé par un bouton, pas caché dans un `<summary>`.
//
// Un seul chemin vers le tableau, donc, et plus deux (la consigne du 12/09 : « ce bouton
// remplace/absorbe les mécanismes de tableau déjà présents — pas deux façons de faire »).

// `titre` (audit 07/09) : le nom accessible du canvas était le même pour tous les graphiques
// (« Graphique de données territoriales ») — la pyramide des âges et la courbe 1876-2023
// étaient indiscernables au lecteur d'écran. Le titre de la carte est passé par ChartCard.
// `instantane` (partage par lien, CDC E2 § 3 et § 6.1, E2-A13) : le graphique d'un lien partagé.
// Il se manipule DANS l'instantané — séries par la légende, période par le curseur, graphique ou
// tableau — sans un seul appel au serveur : tous les comparateurs (communes, échelles ajoutées,
// référents de la pyramide) sont éteints, et rien n'est mémorisé sur le terminal du visiteur.
// `pleinEcran` et `commandes` (Marc, 10/10 : le plein écran, version courte du lot A) : la carte
// qui porte le graphique (ChartCard) pose son bouton « Agrandir » dans la rangée de la bascule,
// et prévient quand elle change de boîte — le graphique se remesure alors (voir plus bas).
// `taille` (lot A, 11/10) : le palier choisi par le lecteur (ChartCard) — `h: 'h'` pose la classe
// `.obs2-chart-wrap.haute` ; tout changement de palier repasse par le même `resize()`.
// `comparaisonsRepliees` (14/10, Olivier) : la tuile est repliée — seul le territoire consulté
// est tracé (graphique, tableau, « Structure ») ; « Comparer à » s'efface sans rien perdre et ses
// choix reviennent au dépli. L'image partagée, elle, rejoue le graphique complet.
export default function ChartBlock({ spec, color, titre, instantane = false, pleinEcran = false,
                                     commandes, taille = null, comparaisonsRepliees = false }:
                                   { spec: ChartSpec; color: string; titre?: string; instantane?: boolean
                                     pleinEcran?: boolean; commandes?: ReactNode
                                     taille?: { l: number; h: 's' | 'h' } | null
                                     comparaisonsRepliees?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<Chart | null>(null)
  const { insee } = useParams()
  const auth = useAuth()
  const theme = useThemeGraphiques()
  // Sans jeton, aucun comparateur ne s'ouvre : c'est le verrou réseau de l'instantané.
  const token = instantane ? null : auth.token

  // ── C1 (plan 12/09) : « Graphique | Tableau » ────────────────────────────────────────
  // La clé identifie LE graphique et non son emplacement : le titre suffit à le distinguer
  // de ses voisins sur la page, et le type + le nombre de séries départagent deux
  // graphiques homonymes de thématiques différentes. Volontairement sans le code INSEE :
  // un lecteur qui a mis « Public / privé par type » en tableau le veut en tableau pour la
  // commune suivante aussi — c'est une préférence de lecture, pas un état de page.
  const [vueChoisie, setVue] = useVueDonnees(`${titre ?? ''}|${spec.type}|${spec.datasets.length}`,
                                             !instantane)
  // La route print ignore le choix : le PDF est une capture déterministe de la vue par
  // défaut, et il reçoit SON tableau en plus du tracé (A27), pas à la place.
  const vue = isPrint ? 'graphique' : vueChoisie

  // ── A11 (plan 12/09) : décocher une série filtre AUSSI le tableau ─────────────────────
  // Chart.js tient son propre état de visibilité, que React ne voit pas. On garde donc en
  // parallèle les libellés décochés — c'est eux que le tableau consulte. Deux registres,
  // parce que la légende ne désigne pas la même chose selon le type : une SÉRIE sur un
  // histogramme ou une courbe (une colonne du tableau), une CATÉGORIE sur un anneau (une
  // ligne).
  const [seriesMasquees, setSeriesMasquees] = useState<Set<string>>(new Set())
  const [pointsMasques, setPointsMasques] = useState<Set<string>>(new Set())
  // Lu depuis l'effet de construction du graphe, qui ne se réexécute pas quand ces états
  // changent (le graphe n'a pas à être reconstruit pour un simple masquage) — une ref
  // évite d'ajouter ces états aux dépendances, ce qui détruirait le graphe à chaque clic.
  const masqueRef = useRef<{ series: Set<string>; points: Set<string> }>({
    series: seriesMasquees, points: pointsMasques })
  masqueRef.current = { series: seriesMasquees, points: pointsMasques }

  // ── Switches de la pyramide (demande Marc 31/07 soir, capture dem-2) ──────────────────
  // Deux contrôles, cachés sur la route print (PDF déterministe = vue par défaut) :
  //   · granularité : tranches quinquennales (défaut) ↔ âge par âge (spec.detail) ;
  //   · comparaison : contour V1 d'un référent (France / strate / EPCI), en % de la
  //     population de chaque territoire — un absolu ne se compare jamais (doctrine lab).
  const [gran, setGran] = useState<'quinq' | 'age'>('quinq')
  const [cible, setCible] = useState<string>('aucune')      // id de CIBLES_PYR ou `c:INSEE`
  const [refs, setRefs] = useState<Record<string, PyramideRef | null>>({})
  const [refErreur, setRefErreur] = useState<string | null>(null)
  const aSwitches = spec.type === 'pyramid' && !isPrint && (!!spec.detail || (!!spec.comparable && !!token))

  // ── Communes proposables (#112) — témoins choisis (jetons) + proposés, partagé par la
  // pyramide, la courbe longue et les empilés. 403 (compte gratuit) → liste vide en
  // silence : les cibles agrégées restent servies, seules les communes sont abonné.
  const estHisto = !!spec.histo_comparable && spec.type === 'line'
  const barresKind = spec.barres_comparables
  const besoinCommunes = !isPrint && !!token && !!insee
    && (!!spec.comparable || estHisto || !!barresKind)
  const [communesDispo, setCommunesDispo] = useState<CommuneDispo[] | null>(null)
  useEffect(() => {
    if (!besoinCommunes || communesDispo !== null) return
    axios.get<{ communes: CommuneDispo[] }>(`${API}/observatoire/${insee}/comparaison-communes`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCommunesDispo(r.data.communes || []))
      .catch(() => setCommunesDispo([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [besoinCommunes, insee])

  // ── Courbe longue (#112) : curseur d'année de départ + communes comparées ────────────
  const [anneeDebut, setAnneeDebut] = useState<number | null>(null)
  // Swap effectifs ↔ base 100 (demande Marc 03/08 soir n°3) : la base 100 compare des
  // RYTHMES, les effectifs comparent des TAILLES — deux questions d'élu distinctes, donc
  // deux modes, et non un basculement automatique dès qu'on ajoute une comparaison.
  const [histoMode, setHistoMode] = useState<'valeur' | 'base100'>('valeur')
  const [lectureAnnuelle, setLectureAnnuelle] = useState<'annuelle' | 'moyenne'>('annuelle')
  const moyenneActive = !!spec.moyenne_mobile && lectureAnnuelle === 'moyenne'
  const [histoCibles, setHistoCibles] = useState<{ code: string; nom: string
    annees: number[]; valeurs: (number | null)[] }[]>([])
  const [compInfo, setCompInfo] = useState<string | null>(null)
  // Le nom de la cible en cours d'ajout (P1) : « Ajout de Longjumeau… » tant que le serveur
  // n'a pas répondu ; le sélecteur est désactivé pendant ce temps.
  const [compChargement, setCompChargement] = useState<string | null>(null)
  const nomCible = (code: string) =>
    HIST_ECHELLES.find(c => c.id === code)?.label
    ?? communesDispo?.find(c => c.code === code)?.nom ?? code
  const ajouterHisto = (code: string) => {
    if (!code || histoCibles.length >= HISTO_MAX || histoCibles.some(c => c.code === code)) return
    // `e:epci`… = échelle agrégée (pas d'auth requise) ; sinon commune témoin (abonné).
    const params: Record<string, string> = code.startsWith('e:')
      ? { echelle: code.slice(2) } : { commune: code }
    if (spec.histo_graphe) params.graphe = spec.histo_graphe
    const nom = nomCible(code)
    setCompInfo(null); setCompChargement(nom)
    axios.get(`${API}/observatoire/${insee}/historique-compare`,
      { params, headers: token ? { Authorization: `Bearer ${token}` } : undefined })
      .then(r => {
        if (r.data.indisponible) setCompInfo(`${r.data.nom} : ${r.data.indisponible}`)
        else if (!aUneValeur(r.data.valeurs)) setCompInfo(`${r.data.nom} : ${SANS_VALEUR}`)
        else setHistoCibles(prev => (prev.length >= HISTO_MAX || prev.some(c => c.code === code))
          ? prev : [...prev, r.data])
      })
      .catch(e => setCompInfo(`${nom} : ${messageErreur(e)}`))
      .finally(() => setCompChargement(null))
  }
  // Empilés : masquage de barres à la convenance (vague 3 — « la lisibilité de la
  // comparaison serait plus simple »).
  const [barresMasquees, setBarresMasquees] = useState<Set<string>>(new Set())

  // ── Empilés 100 % (#112) : une barre de plus par commune témoin ─────────────────────
  const [barresAjout, setBarresAjout] = useState<{ code: string; nom: string
    parts: (number | null)[] }[]>([])
  const ajouterBarre = (code: string) => {
    if (!code || !barresKind || barresAjout.length >= BARRES_MAX
        || barresAjout.some(b => b.code === code)) return
    const nom = nomCible(code)
    setCompInfo(null); setCompChargement(nom)
    axios.get(`${API}/observatoire/${insee}/barres-comparees`,
      { params: { graphe: barresKind, commune: code },
        headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data.indisponible) setCompInfo(`${r.data.nom} : ${r.data.indisponible}`)
        else if (!aUneValeur(r.data.parts)) setCompInfo(`${r.data.nom} : ${SANS_VALEUR}`)
        else setBarresAjout(prev => (prev.length >= BARRES_MAX || prev.some(b => b.code === code))
          ? prev : [...prev, r.data])
      })
      .catch(e => setCompInfo(`${nom} : ${messageErreur(e)}`))
      .finally(() => setCompChargement(null))
  }
  // Ce que le sélecteur ne propose plus : les cibles déjà ajoutées (un doublon ne faisait
  // rien), et plus rien du tout au plafond — la raison s'écrit à côté (P1).
  const dejaAjoute = (code: string) => estHisto
    ? histoCibles.some(c => c.code === code) : barresAjout.some(b => b.code === code)
  const plafondAtteint = estHisto
    ? histoCibles.length >= HISTO_MAX : !!barresKind && barresAjout.length >= BARRES_MAX

  // ── Lot B (CDC du 09/10, M2) : le cadre « Comparer à » ──────────────────────────────
  // Le sélecteur ne s'ouvre que là où il a quelque chose à proposer (échelles de la courbe
  // longue, ou témoins d'un compte) et jamais dans un instantané partagé.
  const selecteurOuvert = !instantane && (estHisto || (!!token && !!communesDispo?.length))
  // La règle se lit AVANT d'atteindre le plafond, tirée des constantes qui l'appliquent.
  const regleComparer = estHisto
    ? `${HISTO_MAX} courbes ajoutées au plus, échelles ou communes`
    : `${BARRES_MAX} communes au plus`
  // La couleur d'une série telle que Chart.js la trace (même fonction, même rang).
  const couleurDe = (label: string, i: number) =>
    couleurSerie(label, i, color, spec.palette, theme)
  // « Ma commune » : la première série. Pas sur un empilé 100 % : la commune y est une
  // BARRE, déjà présente parmi les puces bascules, et une barre porte toutes les couleurs
  // de ses catégories — aucune pastille ne la résume.
  const principaleComparer = barresKind && spec.empile ? undefined
    : { pastilles: [{ couleur: couleurDe(spec.datasets[0]?.label ?? '', 0) }] }
  const pucesComparer: PuceComparee[] = barresKind && spec.empile
    // Empilés : UNE SEULE puce par barre (recette Marc 03/08, capture) — le libellé
    // active/désactive, la croix (communes ajoutées seulement) supprime. Avant, une commune
    // ajoutée apparaissait DEUX fois : sa puce d'activation et sa pastille de suppression.
    ? [...spec.labels.map(l => ({ label: String(l), code: null as string | null })),
       ...barresAjout.map(b => ({ label: b.nom, code: b.code }))].map(({ label, code }) => ({
        cle: label, libelle: label, actif: !barresMasquees.has(label),
        onBasculer: () => setBarresMasquees(p => {
          const n = new Set(p); if (n.has(label)) n.delete(label); else n.add(label); return n
        }),
        onRetirer: code ? () => {
          setBarresAjout(p => p.filter(x => x.code !== code))
          // le masquage suit la barre : sans ce nettoyage, une commune masquée puis retirée
          // revenait invisible si on la rajoutait
          setBarresMasquees(p => { const n = new Set(p); n.delete(label); return n })
        } : undefined,
      }))
    // Courbe longue : la série k+1 est la k-ième cible ajoutée (« Ma commune » en tête).
    : estHisto ? histoCibles.map((c, k) => ({
        cle: c.code, libelle: c.nom, pastilles: [{ couleur: couleurDe(c.nom, 1 + k) }],
        onRetirer: () => setHistoCibles(p => p.filter(x => x.code !== c.code)),
      }))
    // Groupés : la commune ajoutée est une série de plus, après celles du graphique.
    : barresAjout.map((b, k) => ({
        cle: b.code, libelle: b.nom,
        pastilles: [{ couleur: couleurDe(b.nom, spec.datasets.length + k) }],
        onRetirer: () => setBarresAjout(p => p.filter(x => x.code !== b.code)),
      }))
  // Le nom d'un référent de la pyramide : la commune choisie, ou le nom RÉEL du périmètre dès
  // qu'une réponse l'a livré (Marc 06/08), le rôle générique sinon — utilisable avant tout
  // chargement.
  const libelleReferent = (id: string) => {
    if (id.startsWith('c:')) return communesDispo?.find(c => c.code === id.slice(2))?.nom || id.slice(2)
    const reel = (id === 'epci' || id === 'departement')
      ? Object.values(refs).find(r => r?.[id]?.libelle)?.[id]?.libelle : undefined
    return reel || CIBLES_PYR.find(c => c.id === id)?.label || id
  }

  // La clé du référent courant dans `refs` — la même que celle que l'effet remplit, pour que
  // l'écran sache dire « chargement » tant qu'elle est vide (P1, pyramide).
  const clePyr = cible.startsWith('c:')
    ? `${gran === 'age' ? 'age' : 'quinquennal'}|${cible}` : (gran === 'age' ? 'age' : 'quinquennal')
  useEffect(() => {
    if (cible === 'aucune' || !insee || !token) { setRefErreur(null); return }
    const g = gran === 'age' ? 'age' : 'quinquennal'
    const cle = cible.startsWith('c:') ? `${g}|${cible}` : g
    if (refs[cle]) { setRefErreur(null); return }
    const params: Record<string, string> = { granularite: g }
    if (cible.startsWith('c:')) params.commune = cible.slice(2)
    axios.get<PyramideRef>(`${API}/observatoire/${insee}/pyramide-comparee`,
      { params, headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setRefs(prev => ({ ...prev, [cle]: r.data })); setRefErreur(null) })
      .catch(() => setRefErreur('comparaison indisponible'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cible, gran, insee, token])

  // Le spec EFFECTIF : granularité choisie, bascule en % quand on compare, contours.
  // (`effectifComplet` : avant le repli des comparaisons, voir plus bas.)
  const effectifComplet = useMemo<ChartSpec>(() => {
    if (spec.type !== 'pyramid') {
      // GROUPÉ comparable (dem-3) : une commune ajoutée est une SÉRIE de plus — les
      // échelles se masquent par la légende Chart.js, pas par les puces.
      if (barresKind && !spec.empile && barresAjout.length > 0) {
        return { ...spec, datasets: [...spec.datasets,
          ...barresAjout.map(b => ({ label: b.nom, data: b.parts }))] }
      }
      // EMPILÉ 100 % : une BARRE ajoutée par commune témoin (mêmes catégories, mêmes
      // dénominateurs — le serveur calcule avec le code des barres de l'écran), et les
      // puces masquent les barres à la convenance.
      if (barresKind && spec.empile && (barresAjout.length > 0 || barresMasquees.size > 0)) {
        const tousLabels = [...spec.labels.map(String), ...barresAjout.map(b => b.nom)]
        const plein = spec.datasets.map((ds, i) =>
          [...ds.data, ...barresAjout.map(b => b.parts[i] ?? null)])
        // A1 (12/08) : `abs` (absolus d'infobulle) se réindexe AVEC `data` — sans ça,
        // masquer ou ajouter une barre décalait les absolus d'une barre sur l'autre
        // (le témoin ajouté n'a pas d'absolu : l'endpoint ne sert que les parts).
        const pleinAbs = spec.datasets.map(ds =>
          ds.abs ? [...ds.abs, ...barresAjout.map(() => null)] : null)
        const keep = tousLabels.map((l, idx) => ({ l, idx }))
          .filter(x => !barresMasquees.has(x.l))
        return { ...spec, labels: keep.map(x => x.l),
          datasets: spec.datasets.map((ds, i) => ({
            ...ds, data: keep.map(x => plein[i][x.idx]),
            ...(pleinAbs[i] ? { abs: keep.map(x => pleinAbs[i]![x.idx]) } : {}) })) }
      }
      if (!estHisto) return spec
      // Courbe longue : fenêtre depuis l'année choisie ; en comparaison multi-communes,
      // base 100 à la première valeur visible de CHAQUE série (deux tailles de communes
      // ne se lisent pas en absolu sur un même axe) — l'absolu reste au survol (abs).
      const annees = spec.labels.map(Number)
      const debut = anneeDebut ?? annees[0]
      const i0 = Math.max(0, annees.findIndex(a => a >= debut))
      const labels = spec.labels.slice(i0)
      // Calcul sur TOUT l'historique, avant de couper la fenêtre : déplacer « Depuis »
      // ne doit pas effacer les deux premières moyennes pourtant connues.
      const partielles = new Set(spec.annee_partielle != null ? [spec.annee_partielle] : [])
      const lecture = (ans: (string | number)[], vals: (number | null)[]) =>
        moyenneActive ? moyenneMobile3Ans(ans, vals, partielles) : vals
      // Revue Codex du 20/10 (P2) : la moyenne reste EXACTE pour la base 100 (arrondir avant
      // de normaliser faussait les petites communes : 100 → 200 au lieu de 100 → 150) ; seules
      // les valeurs AFFICHÉES en logements sont arrondies à l'unité.
      const affiche = (vals: (number | null)[]) =>
        moyenneActive ? vals.map(v => (v === null ? null : Math.round(v))) : vals
      const principal = lecture(spec.labels, spec.datasets[0].data).slice(i0)
      if (histoCibles.length === 0 && histoMode === 'valeur') {
        if (i0 === 0 && !moyenneActive) return spec
        return { ...spec, labels, datasets: [{ ...spec.datasets[0], data: affiche(principal) }] }
      }
      const serieDe = (annees_s: number[], vals: (number | null)[]) => {
        const transformees = lecture(annees_s, vals)
        const par = new Map(annees_s.map((a, i) => [a, transformees[i]]))
        return labels.map(l => par.get(Number(l)) ?? null)
      }
      const toutes = [
        { nom: 'Ma commune', brut: principal },
        ...histoCibles.map(c => ({ nom: c.nom, brut: serieDe(c.annees, c.valeurs) })),
      ]
      if (histoMode === 'valeur') {
        return { ...spec, labels,
                 datasets: toutes.map(s => ({ label: s.nom, data: affiche(s.brut) })) }
      }
      const datasets = toutes.map(s => {
        const base = s.brut.find(v => v !== null && v !== 0) as number | undefined
        return { label: s.nom,
                 data: s.brut.map(v => (v !== null && base ? Math.round(1000 * v / base) / 10 : null)),
                 abs: affiche(s.brut), absLabel: (moyenneActive ? 'moyenne mobile sur 3 ans, ' : '')
                   + (spec.histo_unite ?? 'hab') }
      })
      return { ...spec, labels, datasets, unit: '' }
    }
    const base = gran === 'age' && spec.detail
      ? { ...spec, labels: spec.detail.labels, datasets: spec.detail.datasets }
      : spec
    if (cible === 'aucune') return base
    const g = gran === 'age' ? 'age' : 'quinquennal'
    // MÊME clé que l'écriture (revue codex P1, 03/08) : une cible commune est stockée
    // sous `g|c:INSEE` — la lire sous `g` seul rendait l'overlay commune invisible.
    const ref = refs[cible.startsWith('c:') ? `${g}|${cible}` : g]
    if (!ref) return base   // référent en cours de chargement : on reste en absolu
    // Le profil de la COMMUNE vient du SERVEUR (revue Codex P2) : normalisé sur les
    // flottants d'origine — re-normaliser les entiers arrondis du graphique distordait
    // la silhouette des petites communes (202 arrondis cumulés en âge par âge).
    // Les grilles du serveur sont ASC (0 → 100+) ; la pyramide s'affiche DESC — on renverse.
    const desc = (a: number[]) => [...a].reverse()
    const enPct = [
      { ...base.datasets[0], data: desc(ref.commune.hommes_pct).map(v => -v) },
      { ...base.datasets[1], data: desc(ref.commune.femmes_pct) },
    ]
    const cData = cible.startsWith('c:') ? ref.commune_cible
      : ref[cible as 'epci' | 'departement' | 'strate' | 'france']
    if (!cData || !cData.tranches.length) return { ...base, datasets: enPct, unit: '%' }
    const cLbl = cible.startsWith('c:')
      ? (communesDispo?.find(c => c.code === cible.slice(2))?.nom || cible.slice(2))
      : (cData.libelle || CIBLES_PYR.find(c => c.id === cible)?.label || cible)
    const h = desc(cData.hommes_pct).map(v => -v)
    const f = desc(cData.femmes_pct)
    const contour = (lab: string, data: number[]) => ({
      label: lab, data, type: 'line' as const, borderColor: theme.contour, borderWidth: 2,
      pointRadius: 0, pointHitRadius: 12, stepped: 'middle' as const, fill: false,
    })
    return {
      ...base, unit: '%',
      datasets: [...enPct,
        contour(`${cLbl} — contour (H)`, h) as unknown as ChartSpec['datasets'][number],
        contour(`${cLbl} — contour (F)`, f) as unknown as ChartSpec['datasets'][number]],
    }
  }, [spec, gran, cible, refs, communesDispo, estHisto, barresKind,
      anneeDebut, histoMode, histoCibles, barresAjout, barresMasquees, theme, moyenneActive])

  // ── Repli des comparaisons (14/10, Olivier) ────────────────────────────────────────────
  // Replié, le graphique tracé est le graphique SERVI réduit au territoire consulté — pas
  // l'effectif : une commune ajoutée par « Comparer à » est une comparaison, et une barre de la
  // commune masquée par sa puce laisserait un graphique vide. Ces choix restent en mémoire et
  // reviennent au dépli. Jamais sur la route print ni dans un instantané partagé.
  const axeComparaisons = useMemo(() => axeTerritoires(spec), [spec])
  const replie = comparaisonsRepliees && !!axeComparaisons && !isPrint && !instantane
  const specReplie = useMemo(() => (replie ? replierComparaisons(spec) : null), [replie, spec])
  const effectif = specReplie ?? effectifComplet
  // Les territoires étaient des SÉRIES : une série masquée par la légende ne vaut plus rien sur
  // une tuile qui n'en trace qu'une (sans légende pour la réafficher). Sur un empilé, les
  // séries sont des catégories : leurs masques tiennent.
  const seriesVues = replie && axeComparaisons === 'series' ? AUCUNE_SERIE : seriesMasquees
  masqueRef.current = { series: seriesVues, points: pointsMasques }
  // L'export complet lit les masques du graphique déplié, au moment du partage.
  const seriesCompletesRef = useRef(seriesMasquees)
  seriesCompletesRef.current = seriesMasquees

  // C7 reste (recette SITADEL 11/08) : en base 100, une série entièrement à zéro sur la
  // fenêtre n'a pas de première valeur pour s'indexer — elle disparaissait EN SILENCE.
  // Détection sur les datasets effectivement rendus (pas de duplication de la logique de
  // fenêtrage) ; la note sous le graphe nomme les séries que la base 100 ne peut pas
  // tracer, au lieu de les laisser manquer sans explication.
  const seriesNonIndexables = useMemo(() => {
    if (!estHisto || histoMode !== 'base100') return []
    return effectif.datasets.filter(serieNonIndexable).map(ds => ds.label)
  }, [effectif, estHisto, histoMode])

  // ── P24 (démo du 09/10, D4) : « Barres | Structure » ────────────────────────────────
  // L'anneau d'une répartition d'UN territoire (`obs/tableau/structure`). Le spec tracé
  // devient alors un anneau ; tout le reste (étiquettes en %, infobulle, légende filtrante,
  // tableau) est le chemin des anneaux servis par le serveur, déjà rodé — rien de neuf à
  // dessiner. Jamais sur la route print : le PDF rend la vue par défaut.
  //
  // Marc, 13/10 (23 h) : plusieurs territoires tracés (l'âge ou le zonage ABC par échelles,
  // une commune ajoutée) ne désactivent plus « Structure ». L'anneau montre UNE strate — par
  // défaut la première tracée, la commune —, les autres se choisissent d'un clic au-dessus
  // de l'anneau. « Comparer à » n'est jamais modifié : « Barres » rend le graphique comparé.
  // Le nom de la commune, quand la page le fournit (ThemeView, lien partagé) : il remplace
  // « Ma commune » au centre de l'anneau et sur sa puce (revue de #773).
  const nomCommune = useContext(NomCommuneCtx)
  const etatStr = useMemo(() => etatStructure(spec, effectif, nomCommune),
                          [spec, effectif, nomCommune])
  const [formeChoisie, setFormeChoisie] = useFormeRepartition(
    `${titre ?? ''}|${spec.type}|${spec.datasets.length}`, !instantane)
  // Revue codex de #773 (2e passe) : le choix est rangé AVEC sa page et son graphique — un
  // ChartBlock réutilisé pour une autre commune (même département, même EPCI) rouvrirait sinon
  // l'anneau sur le territoire choisi ailleurs, au lieu de la commune.
  const cleStrate = `${insee ?? ''}|${titre ?? ''}|${spec.type}`
  const [choixStrate, setChoixStrate] = useState<{ cle: string; strate: string } | null>(null)
  const strateChoisie = choixStrate?.cle === cleStrate ? choixStrate.strate : null
  const setStrateChoisie = (s: string) => setChoixStrate({ cle: cleStrate, strate: s })
  // Revue codex de #773 (3e passe) : les masquages des BARRES, mis de côté pendant l'anneau
  // (`choisirForme`) — une commune comparée cachée par la légende n'y devient pas une strate,
  // et « Barres » la rend cachée, comme le lecteur l'avait laissée.
  const [masquesBarres, setMasquesBarres] =
    useState<{ series: Set<string>; points: Set<string> } | null>(null)
  const toutesStrates = etatStr.etat === 'ok' ? etatStr.strates : []
  const stratesVisibles = toutesStrates.filter(s => spec.empile || !masquesBarres?.series.has(s.cle))
  // Tout caché sur les barres (4e passe) : l'anneau montre quand même la commune — jamais un
  // bouton « Structure » enfoncé sur des barres —, et elle SEULE (5e passe) : une comparaison
  // cachée ne redevient pas un choix.
  const strates = stratesVisibles.length ? stratesVisibles : toutesStrates.slice(0, 1)
  // Une strate choisie qui n'est plus tracée (barre masquée, commune retirée) : la première.
  const strate = strates.find(s => s.cle === strateChoisie) ?? strates[0]
  const structureActive = !isPrint && formeChoisie === 'structure' && !!strate
  const specTrace = useMemo(
    () => (structureActive && strate ? specStructure(spec, strate.structure) : null),
    [structureActive, strate, spec])
  const centre = structureActive ? strate?.nom : undefined
  // Passer d'une forme à l'autre change ce que la légende désigne (une série d'un empilé
  // devient une part de l'anneau) : l'anneau part de tout visible, graphique ET tableau. Les
  // masquages des barres sont mis de côté et rendus au retour (revue codex de #773).
  const choisirForme = (f: typeof formeChoisie) => {
    if (f === formeChoisie) return
    if (f === 'structure') {
      setMasquesBarres({ series: seriesMasquees, points: pointsMasques })
      setSeriesMasquees(new Set()); setPointsMasques(new Set())
    } else {
      setSeriesMasquees(masquesBarres?.series ?? new Set())
      setPointsMasques(masquesBarres?.points ?? new Set())
      setMasquesBarres(null)
    }
    setFormeChoisie(f)
  }
  // Les noms du spec servi, pour l'effet qui reprend `spec` / `effectif` (voir plus bas).
  const specServi = spec
  const effectifServi = effectif

  useEffect(() => {
    if (!canvasRef.current) return
    const toile = canvasRef.current
    // La configuration Chart.js d'un graphique — celle de l'écran, et (repli des comparaisons,
    // 14/10) celle du graphique complet que l'image partagée rejoue. `specTrace` : l'anneau
    // « Structure » tracé à la place, `null` pour l'export complet.
    const configDe = (spec: ChartSpec, effectif: ChartSpec, specTrace: ChartSpec | null) => {
    const doughnutPalette = theme.anneau
    const isPyramid = spec.type === 'pyramid'
    const baseType = isPyramid ? 'bar' : spec.type
    // La forme d'échelles, décidée une fois et nommée (#260) — elle gouverne le bloc
    // `scales`, le placement des étiquettes et le calcul de la part.
    const forme = formeEchelles(spec)

    // Palette sortie de l'effet (lot B, 11/10) : le cadre « Comparer à » pose sur chaque puce
    // une pastille de la couleur de SA série — même fonction, même thème, jamais recopiée.
    const serieColor = (label: string, i: number) =>
      couleurSerie(label, i, color, spec.palette, theme)

    const enComparaison = isPyramid && effectif.unit === '%'
    // Graphique à panneaux : barres verticales simples seulement (ni pyramide, ni empilé, ni
    // horizontal) ; une série par panneau, son axe s'appelle `y`, `y1`, `y2`…
    const panneaux = !!spec.panneaux && forme === 'standard' && baseType === 'bar' && !spec.empile
    const axePanneau = (i: number) => (i === 0 ? 'y' : `y${i}`)
    const datasets = effectif.datasets.map((ds, i) => ({
      ...ds,
      backgroundColor: spec.type === 'doughnut' ? doughnutPalette
        : isPyramid ? (i === 0 ? theme.pyramide[0] : i === 1 ? theme.pyramide[1] : 'transparent')
        : serieColor(ds.label, i),
      borderColor: baseType === 'line' ? serieColor(ds.label, i)
        : isPyramid ? (ds as { borderColor?: string }).borderColor : serieColor(ds.label, i),
      borderWidth: baseType === 'line' ? 2.5
        : (ds as { borderWidth?: number }).borderWidth ?? 0,
      pointBackgroundColor: serieColor(ds.label, i),
      pointRadius: baseType === 'line' ? 3 : 0,
      tension: 0.3,
      fill: false,
      borderRadius: baseType === 'bar' && !isPyramid ? 4 : 0,
      ...(isPyramid ? { barPercentage: 0.92, categoryPercentage: 0.86 } : {}),
      // `grouped: false` (revue codex du 14/10, P1) : sans lui, Chart.js range les barres d'une
      // même année CÔTE À CÔTE comme deux séries d'un même axe, et les panneaux se décalent
      // d'une demi-barre ; chaque barre doit être centrée sur SON année, l'une au-dessus de l'autre.
      ...(panneaux ? { yAxisID: axePanneau(i), grouped: false } : {}),
    }))

    // Échelle : une série d'évolution (line) ne DOIT PAS partir de zéro — sinon
    // 35 743 → 37 471 devient une ligne plate collée en haut (bug remonté Marc).
    // `grace` élargi : l'étiquette se pose 11 px AU-DESSUS du point, il faut donc de la
    // place au-dessus du maximum, sinon elle sort du cadre et le plugin la sacrifie.
    //
    // MAIS `grace` élargit AUSSI vers le bas (recette Marc 02/08 sur la consommation
    // foncière : « je ne comprends pas pourquoi on peut avoir une échelle négative »).
    // Quand la plus petite valeur frôle zéro, 18 % de l'amplitude font plonger l'axe dans
    // le négatif — or aucune surface consommée n'est négative, l'axe racontait une
    // impossibilité physique. On plafonne donc le bas à zéro DANS CE CAS SEULEMENT :
    //   · série d'évolution démographique (35 743…37 471) → bas gracieux à 35 432 > 0,
    //     rien ne change, la courbe garde son amplitude ;
    //   · série de consommation (0…723 346)             → bas gracieux négatif, on le
    //     ramène à 0 ;
    //   · série qui porte de VRAIES valeurs négatives (soldes) → intacte, on n'y touche
    //     jamais, un solde négatif doit se voir.
    const valeursNum = effectif.datasets
      .flatMap(d => d.data as (number | null)[])
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    const dataMin = valeursNum.length ? Math.min(...valeursNum) : 0
    const dataMax = valeursNum.length ? Math.max(...valeursNum) : 0
    const plage = dataMax - dataMin
    // Cas dégénéré signalé par la revue croisée du 02/08 : une série ENTIÈREMENT à zéro
    // a une plage nulle, donc « bas gracieux » = 0, donc pas de plancher posé — et
    // Chart.js, privé d'échelle, invente alors un axe −1…1. La commune qui n'a rien
    // consommé retrouvait exactement l'axe négatif qu'on venait de supprimer.
    // Plage nulle → on décide sur la valeur elle-même ; une série constante à 35 743
    // garde son cadrage libre, une série constante à 0 se voit poser son plancher.
    const basGracieuxNegatif = plage > 0 ? dataMin - 0.18 * plage < 0 : dataMin <= 0
    const lineScale = {
      beginAtZero: false, grace: '18%' as const,
      ...(dataMin >= 0 && basGracieuxNegatif ? { min: 0 } : {}),
    }
    // Règle d'axes ajustés (maquettes de /admin/design seulement, `theme.axesAjustes`) : le
    // site garde ses barres posées sur zéro tant que la direction n'est pas choisie.
    const basAjuste = theme.axesAjustes && baseType === 'bar' && !isPyramid && !spec.empile
      && !panneaux ? basAxeAjuste(dataMin, dataMax) : null
    const barScale = basAjuste === null ? { beginAtZero: true, grace: '14%' as const }
      : { beginAtZero: false, min: basAjuste, grace: '14%' as const }
    const police = (size: number) => (theme.police ? { size, family: theme.police } : { size })
    const avecUnite = (v: number | string, u?: string) => `${fmt(Number(v))}${u ? ` ${u}` : ''}`
    // Les axes des panneaux : une même pile (`stack`), donc des aires l'une SOUS l'autre, jamais
    // superposées. Chart.js range une pile de gauche par poids décroissant, de haut en bas : la
    // première série a le poids le plus fort, elle est en haut. Chaque axe part de zéro (des
    // décomptes et des surfaces) et porte l'unité de SA série.
    const axesPanneaux = panneaux ? Object.fromEntries(effectif.datasets.map((ds, i) => [
      axePanneau(i), {
        beginAtZero: true, grace: '14%' as const,
        stack: 'panneaux', stackWeight: 1, weight: effectif.datasets.length - i,
        // la marge haute (`grace`) de chaque panneau le sépare de celui du dessus : l'étiquette
        // de son plus haut sommet ne touche jamais le pied du panneau précédent
        grid: { color: theme.grille },
        ticks: { font: police(11), maxTicksLimit: 4,
                 // Le repère du HAUT d'un panneau inférieur se poserait sur le zéro du panneau
                 // du dessus (« 3 ha » sur « 0 feux », vu à l'écran) : sa ligne reste, son
                 // libellé se tait.
                 callback: (v: number | string, k?: number, reperes?: unknown[]) =>
                   i > 0 && reperes && k === reperes.length - 1 ? ''
                     : avecUnite(v, ds.unit ?? effectif.unit) },
      }])) : null

    const config: ChartConfiguration<'bar' | 'line' | 'doughnut'> = {
      type: baseType as 'bar' | 'line' | 'doughnut',
      // A13 : les libellés de catégorie passent par `libelleCategorie`. Un libellé nul ou
      // vide réservait jusqu'ici une colonne SANS NOM — le blanc que Marc a pris pour un
      // espacement cassé sur « Public / privé par type » (Bordeaux). La catégorie est
      // nommée « Non renseigné » : elle porte de vrais établissements, la taire les
      // effacerait. Le correctif de fond est côté données (voir `format.ts`).
      data: { labels: effectif.labels.map(libelleCategorie), datasets },
      plugins: [etiquettesDeValeur, ...(basAjuste === null ? [] : [marqueAxeTronque]),
                ...(specTrace ? [centreAnneau] : [])],
      options: {
        // Lu par le plugin d'étiquettes. `kind` est le type DÉCLARÉ (pyramid compris), pas
        // `baseType` : une pyramide est un bar horizontal, et son étiquette ne se pose pas
        // du tout au même endroit.
        responsive: true,
        maintainAspectRatio: false,
        ...(baseType === 'line' ? { layout: { padding: { top: MARGE_ETIQUETTES_COURBES } } } : {}),
        ...(isPrint ? { animation: false as const, resizeDelay: 0 } : {}),
        // P24 : la légende de l'anneau suit la largeur (plein écran, palier, rotation).
        ...(specTrace ? { onResize: (ch: Chart, taille: { width: number }) => {
          const legende = ch.options.plugins?.legend
          const voulue = taille.width >= LARGEUR_LEGENDE_DROITE ? 'right' : 'bottom'
          if (legende && legende.position !== voulue) { legende.position = voulue; ch.update('none') }
        } } : {}),
        ...(isPyramid || spec.horizontal ? { indexAxis: 'y' as const } : {}),
        // Panneaux : survoler une année donne ses mesures ensemble (feux ET surface).
        ...(panneaux ? { interaction: { mode: 'index' as const, intersect: false } } : {}),
        // T2 (05/08) : barres cliquables — un clic sur une année émet `obs:annee`, que
        // la carte SITADEL écoute pour dérouler le détail de l'année (demande Marc :
        // « cliquer sur les années d'autorisation → la liste des autorisations »).
        ...(spec.clic_annees ? {
          onClick: (_evt: unknown, elements: { index: number }[]) => {
            const el = elements && elements[0]
            if (!el) return
            const annee = String(effectif.labels[el.index] ?? '')
            if (annee) window.dispatchEvent(new CustomEvent('obs:annee', { detail: { annee } }))
          },
        } : {}),
        plugins: {
          // Le mode de lecture accompagne aussi la toile exportée (PNG et SVG).
          subtitle: { display: moyenneActive, text: 'Moyenne mobile sur 3 ans',
                      color: theme.encre, font: police(12), padding: { bottom: 10 } },
          // Lu par le plugin d'étiquettes. `kind` est le type DÉCLARÉ (pyramid compris), pas
          // `baseType` : une pyramide est un bar horizontal, et son étiquette ne se pose pas
          // du tout au même endroit.
          // Étiquettes-valeurs coupées hors vue par défaut : 101 rangées d'âge ou des
          // pourcentages à 3 décimales seraient du bruit — l'infobulle reste.
          obsAxeTronque: { encre: theme.encre, horizontal: forme === 'horizontal' },
          obsCentreAnneau: { texte: centre, encre: theme.encre, police: theme.police },
          obsEtiquettesValeur: { unit: effectif.unit, kind: spec.type,
                                 encre: theme.encre, encreND: theme.encreND, police: theme.police,
                                 zerosMuets: (spec as { zerosMuets?: boolean }).zerosMuets,
                                 // D-8 (#260) : placement au bout de la barre, et la part
                                 // à côté de la valeur quand le total a un sens.
                                 horizontal: forme === 'horizontal',
                                 panneaux,
                                 partTotal: totalPourPart(forme, effectif.datasets,
                                                          estProportion(effectif.unit)),
                                 // Les courbes comparées gardent leurs valeurs : couleur de
                                 // série et anti-collision dans le greffon ci-dessus.
                                 off: (isPyramid && (gran === 'age' || enComparaison))
                                      || !!spec.empile },
          legend: {
            display: effectif.datasets.length > 1 || spec.type === 'doughnut',
            // P24 : l'anneau « Structure » range sa légende À DROITE quand la boîte est large
            // (l'anneau garde sa taille), DESSOUS sur un téléphone (voir `onResize`).
            position: specTrace && legendeADroite(canvasRef.current) ? 'right' : 'bottom',
            labels: { boxWidth: 12, font: police(12),
                      filter: (i) => !String(i.text).endsWith('(F)'),
                      // P24 : chaque entrée porte sa valeur et sa part (« 0-14 ans — 17,2 % ») :
                      // sur un anneau, c'est la légende qui se lit, pas l'arc.
                      ...(specTrace ? { generateLabels: (ch: Chart) => {
                        const base = Chart.overrides.doughnut.plugins.legend.labels
                          .generateLabels(ch) as LegendItem[]
                        return base.map((it, k) => {
                          const v = specTrace.datasets[0].data[k]
                          return typeof v === 'number'
                            ? { ...it, text: texteLegende(String(specTrace.labels[k]), v,
                                                          specTrace, fmt, fmtPart) }
                            : it
                        })
                      } } : {}) },
            // ── A11 : le tableau suit la légende ────────────────────────────────────
            // Le geste natif de Chart.js (masquer la série, masquer la part) est refait
            // ici à l'identique, puis ANNONCÉ à React, qui retire du tableau la colonne
            // ou la ligne correspondante. Sans ça, décocher « Privé » effaçait la courbe
            // mais laissait sa colonne dans le tableau : les deux vues d'une même donnée
            // se contredisaient à l'écran. Le comportement existait déjà sur les barres
            // empilées (leurs puces filtrent `effectif`) — il est ici généralisé au lieu
            // d'être réécrit graphique par graphique.
            onClick: (_evt, item, legend) => {
              const ch = legend.chart
              // Ce que l'item DÉSIGNE (une série ou une catégorie) est décidé par
              // `cibleLegende`, testé à part : c'est la seule logique de ce composant
              // qu'aucun passage au navigateur ne peut atteindre (la légende de Chart.js
              // est dessinée dans le canvas, elle n'a pas d'élément à cliquer).
              const cible = cibleLegende(spec.type, item, effectif.labels,
                                         i => ch.data.datasets[i]?.label)
              if (!cible) return
              if (cible.registre === 'points') {
                ch.toggleDataVisibility(item.index!)
                ch.update()
                setPointsMasques(p => basculer(p, cible.cle))
                return
              }
              const i = item.datasetIndex!
              ch.setDatasetVisibility(i, !ch.isDatasetVisible(i))
              ch.update()
              setSeriesMasquees(p => basculer(p, cible.cle))
            },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const raw = typeof ctx.raw === 'number' ? ctx.raw : null
                if (raw === null) return `${ctx.dataset.label}: N/D`
                if (isPyramid) return `${ctx.dataset.label}: ${fmt(Math.abs(raw))}${enComparaison ? ' %' : ''}`
                if (spec.type === 'doughnut') {
                  // Même garde que le tableau (revue codex P2, 12/09) : la part n'est
                  // calculée que si les données sont des ABSOLUS. Quand l'unité est déjà
                  // une proportion, ce calcul divisait des arrondis par la somme des
                  // arrondis et annonçait « 45 (45,5 %) » sur une valeur servie à 45 % —
                  // un chiffre fabriqué, et l'unité servie perdue au passage.
                  // Corrigé ici ET dans le tableau : les deux vues d'une même donnée ne
                  // peuvent pas se contredire, c'est tout le contrat de la bascule C1.
                  if (estProportion(effectif.unit)) {
                    return `${ctx.label}: ${fmt(raw)}${effectif.unit ? ` ${effectif.unit}` : ''}`
                  }
                  const total = (ctx.dataset.data as number[]).reduce((s, v) => s + (v || 0), 0)
                  const part = total ? (100 * raw / total).toFixed(1) : '0'
                  return `${ctx.label}: ${fmt(raw)} (${part} %)`
                }
                const ds = ctx.dataset as ChartSpec['datasets'][number]
                const absV = ds.abs?.[ctx.dataIndex]
                const absTxt = absV !== null && absV !== undefined
                  ? ` · ${fmt(absV)} ${ds.absLabel || 'faits'}` : ''
                const u = ds.unit ?? effectif.unit
                const principal = `${ctx.dataset.label}: ${fmt(raw)}${u ? ` ${u}` : ''}${absTxt}`
                // La note du point devient des lignes SUPPLÉMENTAIRES de l'infobulle
                // (Chart.js accepte un tableau). Découpée sur ' · ' pour qu'une commune
                // touchée par trois risques la même année les liste, une par ligne, au
                // lieu d'une bouillie qui déborde du cadre.
                const note = ds.notes?.[ctx.dataIndex]
                return note ? [principal, ...String(note).split(' · ').map(s => `· ${s}`)]
                            : principal
              },
            },
          },
        },
        // La FORME décide, et elle est nommée (`obs/tableau/echelles`) : l'invariant est
        // « quel axe porte les valeurs », pas « quelle branche du ternaire ». C'est cet
        // invariant que le défaut de la revue #260 violait — voir la branche 'horizontal'.
        scales: forme === 'anneau' ? undefined : forme === 'pyramide' ? {
          x: { stacked: true, grid: { color: theme.grille },
               ticks: { font: police(11),
                        callback: (v) => `${fmt(Math.abs(Number(v)))}${enComparaison ? ' %' : ''}` } },
          y: { stacked: true, grid: { display: false },
               ticks: { font: police(gran === 'age' ? 8 : 10),
                        autoSkip: gran === 'age', maxTicksLimit: gran === 'age' ? 26 : undefined } },
        } : forme === 'empile-horizontal' ? {
          // Empilé 100 % horizontal (dem-4) : l'axe des VALEURS est x, borné à 100 —
          // sans la borne, les arrondis des parts font déborder la barre de 100,2 %.
          x: { stacked: true, max: 100, grid: { color: theme.grille },
               ticks: { font: police(11),
                        callback: (v) => `${fmt(Number(v))}${effectif.unit ? ` ${effectif.unit}` : ''}` } },
          y: { stacked: true, grid: { display: false }, ticks: { font: police(11) } },
        } : forme === 'horizontal' ? {
          // D-8 (revue codex #260) — barres horizontales NON empilées. Cette branche
          // MANQUAIT : ces graphiques tombaient dans la branche standard, qui pose le
          // formatage des valeurs sur `y`. Avec `indexAxis: 'y'`, `y` est l'axe des
          // CATÉGORIES — Chart.js passait l'indice du tick au formateur et les libellés
          // sortaient « 0 », « 1 », « 2 » au lieu des noms de filières.
          // Aucun des six horizontaux d'aujourd'hui n'est concerné (tous empilés) ; les
          // quatre que livrent A-back-1 et A-back-2 ce soir le sont tous.
          x: { ...barScale, grid: { color: theme.grille },
               ticks: { font: police(11),
                        callback: (v) => `${fmt(Number(v))}${effectif.unit ? ` ${effectif.unit}` : ''}` } },
          y: { grid: { display: false }, ticks: { font: police(11) } },
        } : axesPanneaux ? {
          ...axesPanneaux,
          x: { grid: { display: false }, ticks: { font: police(11) }, ...rappelsDernierRepere() },
        } : {
          y: { ...(baseType === 'line' ? lineScale : barScale),
               grid: { color: theme.grille },
               ticks: { font: police(11),
                        callback: (v) => `${fmt(Number(v))}${effectif.unit ? ` ${effectif.unit}` : ''}` } },
          // #569 : le saut d'étiquettes garde toujours le DERNIER repère — une courbe
          // 2020-2025 étiquetée « 2020, 2022, 2024 » se lisait arrêtée en 2024.
          x: { grid: { display: false }, ticks: { font: police(11) }, ...rappelsDernierRepere() },
        },
      },
    }
    return { config, datasets }
    }

    // P24 : en vue « Structure », l'effet trace l'anneau à la place du graphique servi. Les
    // deux noms sont REPRIS volontairement : chaque test de type de cet effet (étiquettes,
    // infobulle, légende, échelles, masques) doit lire l'anneau, et en oublier un seul
    // mélangerait deux formes dans un même tracé.
    const spec = specTrace ?? specServi
    const effectif = specTrace ?? effectifServi
    const { config, datasets } = configDe(spec, effectif, specTrace)
    chartRef.current?.destroy()
    chartRef.current = new Chart(toile, config)

    // A11, suite : le graphe vient d'être RECONSTRUIT (changement de granularité, ajout
    // d'une comparaison…) et Chart.js repart avec toutes ses séries visibles. Sans cette
    // ré-application, une série décochée revenait tracée alors que le tableau, lui, la
    // tenait toujours pour masquée — les deux vues se seraient contredites au premier
    // changement de spec.
    const masque = masqueRef.current
    if (masque.series.size || masque.points.size) {
      if (spec.type === 'doughnut') {
        effectif.labels.forEach((l, i) => {
          if (masque.points.has(libelleCategorie(l))) chartRef.current?.toggleDataVisibility(i)
        })
      } else {
        datasets.forEach((ds, i) => {
          if (masque.series.has(String(ds.label ?? ''))) chartRef.current?.setDatasetVisibility(i, false)
        })
      }
      chartRef.current?.update()
    }

    // Repli des comparaisons (14/10) : l'image partagée (PNG, SVG) garde les comparaisons. On
    // dépose, pour cette toile, de quoi rejouer le graphique complet — calculé au moment du
    // partage seulement, avec les séries que la légende du graphique déplié masquait. Sur
    // l'anneau « Structure », rien : il ne montre déjà qu'un territoire.
    if (replie && !specTrace) {
      deposerGraphiqueComplet(toile, () => {
        const { config: complet } = configDe(specServi, effectifComplet, null)
        const masquees = seriesCompletesRef.current
        complet.data.datasets = complet.data.datasets.map(d =>
          (masquees.has(String(d.label ?? '')) ? { ...d, hidden: true } : d))
        const horizontal = specServi.type !== 'pyramid' && !!specServi.horizontal
        return { config: complet as ChartConfiguration,
                 hauteur: horizontal ? hauteurHorizontale(effectifComplet.labels.length) : undefined }
      })
    } else {
      deposerGraphiqueComplet(toile, null)
    }

    return () => { chartRef.current?.destroy(); chartRef.current = null; deposerGraphiqueComplet(toile, null) }
  }, [specServi, effectifServi, specTrace, centre, color, gran, theme, replie, effectifComplet])

  // Le canvas reste MONTÉ en vue tableau (son conteneur est seulement masqué) : le
  // démonter viderait `canvasRef` et le retour au graphique laisserait un cadre vide,
  // l'effet de construction n'ayant aucune raison de se réexécuter. Chart.js mesure son
  // conteneur : au retour de `display:none`, on lui redemande sa taille plutôt que de
  // parier sur le ResizeObserver. Même geste à l'entrée et à la sortie du plein écran : la
  // carte n'est pas remontée, elle change de boîte. Idem quand le lecteur change de palier
  // (lot A) : la carte change de colonnes, la boîte de hauteur.
  const largeurChoisie = taille?.l ?? 1
  const haute = taille?.h === 'h'
  useEffect(() => {
    if (vue === 'graphique') chartRef.current?.resize()
  }, [vue, pleinEcran, largeurChoisie, haute])

  // Impression du navigateur (CDC « témoins partout », étape 8). Les communes que le lecteur a
  // ajoutées sont des séries du graphique : elles partent sur papier avec lui, et le cadre
  // « Comparer à » les y nomme (voir `ComparerA.css`). Mais le papier n'a pas la largeur de
  // l'écran, et Chart.js ne se remesure pas de lui-même au passage à l'impression (sa
  // documentation demande de le faire sur `beforeprint`) : le tracé gardait la taille de
  // l'écran, débordait ou s'écrasait. Même geste que le plein écran de #682, qui écoute
  // `beforeprint` : on écoute ici `beforeprint` / `afterprint`, plus la requête `print`, qui
  // change quand la mise en page papier est réellement appliquée (c'est elle qui donne la
  // bonne taille dans Chrome). Remesurer deux fois ne coûte rien.
  // Pas sur la route print : le PDF de l'étude a sa propre mise en page, vérifiée au banc de
  // capture, et ne doit pas changer par ricochet.
  useEffect(() => {
    if (isPrint) return
    const remesurer = () => { chartRef.current?.resize() }
    const papier = typeof window.matchMedia === 'function' ? window.matchMedia('print') : null
    window.addEventListener('beforeprint', remesurer)
    window.addEventListener('afterprint', remesurer)
    papier?.addEventListener?.('change', remesurer)
    return () => {
      window.removeEventListener('beforeprint', remesurer)
      window.removeEventListener('afterprint', remesurer)
      papier?.removeEventListener?.('change', remesurer)
    }
  }, [])

  // Le spec que le tableau montre : celui qui est tracé (l'anneau « Structure » s'il est affiché).
  const tableauTrace = specTrace ?? effectif

  // A25 : la mention « cocher / décocher » n'a de sens que là où la légende est FILTRABLE,
  // c'est-à-dire là où elle porte au moins deux entrées. Sur un anneau, la légende porte
  // les catégories ; ailleurs, les séries. Un graphique à série unique n'affiche pas de
  // légende du tout : lui coller la mention serait une consigne sans objet.
  const legendeFiltrable = specTrace ? specTrace.labels.length > 1
    : spec.type === 'doughnut'
    ? effectif.labels.length > 1
    : effectif.datasets.length > 1

  return (
    <div>
      {/* C1 : la bascule, en haut à droite du graphique, avant tout autre contrôle. */}
      {!isPrint && (
        <div className="obs2-bascule-ligne">
          {commandes}
          {/* P24 : la forme, seulement sur une répartition et en vue graphique. */}
          {vue === 'graphique' && etatStr.etat !== 'absent' && (
            <BasculeStructure forme={formeChoisie} onChange={choisirForme} nom={titre} />
          )}
          <BasculeVue vue={vue} onChange={setVue} nom={titre} />
        </div>
      )}
      {!!spec.moyenne_mobile && estHisto && !isPrint && (
        <div role="group" aria-label="Lecture de la série annuelle" className="obs2-bascule"
             style={{ marginBottom: 8 }}>
          <button type="button" aria-pressed={!moyenneActive}
                  className={!moyenneActive ? 'actif' : ''}
                  onClick={() => setLectureAnnuelle('annuelle')}>Par an</button>
          <button type="button" aria-pressed={moyenneActive}
                  className={moyenneActive ? 'actif' : ''}
                  onClick={() => setLectureAnnuelle('moyenne')}>Moyenne mobile 3 ans</button>
        </div>
      )}
      {moyenneActive && (
        <p className="obs2-card-note">Lecture : moyenne mobile sur 3 ans (N−2, N−1 et N).
          Une valeur apparaît seulement si les trois années sont connues.</p>
      )}
      {/* Courbe longue : curseur d'année et mode de lecture — des réglages de LECTURE, pas
          des cibles de comparaison : ils restent hors du cadre « Comparer à ». */}
      {vue === 'graphique' && estHisto && !isPrint && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                      marginBottom: 6, fontSize: 12.5 }}>
          {/* Curseur masqué sous 3 points (codex L-G P2) : une série DVF communale peut
              n'avoir qu'une ou deux années — labels[length-2] devenait NaN en max. */}
          {spec.labels.length >= 3 && (
            <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              Depuis
              <input type="range" min={Number(spec.labels[0])}
                     max={Number(spec.labels[spec.labels.length - 2])}
                     value={anneeDebut ?? Number(spec.labels[0])}
                     onChange={e => setAnneeDebut(Number(e.target.value))}
                     aria-label="Année de départ de la courbe" style={{ width: 150 }} />
              <strong>{anneeDebut ?? Number(spec.labels[0])}</strong>
            </label>
          )}
          <div role="group" aria-label="Mode de lecture de la courbe"
               style={{ display: 'inline-flex', border: '1px solid var(--c-border, #E2E8F0)',
                        borderRadius: 6, overflow: 'hidden' }}>
            {([['valeur', spec.histo_unite ? `Valeurs (${spec.histo_unite})` : 'Effectifs'],
               ['base100', 'Base 100']] as const).map(([id, lab]) => (
              <button key={id} onClick={() => setHistoMode(id)}
                      aria-pressed={histoMode === id}
                      style={{ padding: '3px 10px', border: 'none', cursor: 'pointer',
                               background: histoMode === id ? color : 'transparent',
                               color: histoMode === id ? '#fff' : 'inherit' }}>{lab}</button>
            ))}
          </div>
        </div>
      )}
      {/* Lot B (CDC du 09/10, M2) : « Comparer à » dans un vrai cadre — la commune, puis
          chaque cible ajoutée avec la pastille de SA série, puis le sélecteur ; dessous, la
          règle du plafond et les messages de P1 (attente, plafond, erreurs), inchangés. */}
      {/* P24, suite (13/10) : sur l'anneau, les territoires se choisissent un par un — le cadre
          « Comparer à » (qui ajoute et masque des barres) s'efface sans rien perdre : ses
          choix reviennent tels quels avec « Barres ». Deux rangées de puces des mêmes
          territoires, aux gestes différents, se seraient contredites. */}
      {vue === 'graphique' && structureActive && strates.length > 1 && strate && (
        <ChoixStrate strates={strates} choisie={strate.cle} onChange={setStrateChoisie} nom={titre} />
      )}
      {/* Repli des comparaisons (14/10) : le cadre s'efface avec elles, ses choix restent. */}
      {vue === 'graphique' && (estHisto || !!barresKind) && !isPrint && !structureActive && !replie
        && (selecteurOuvert || pucesComparer.length > 0) && (
        <ComparerA principale={principaleComparer} puces={pucesComparer}
                   regle={selecteurOuvert ? regleComparer : undefined}
                   messages={(compChargement || plafondAtteint || compInfo) ? (
                     <>
                       {/* P1 : l'attente et le plafond se lisent — avant, l'un et l'autre étaient muets. */}
                       {compChargement && (
                         <span role="status" aria-live="polite">Ajout de {compChargement}…</span>
                       )}
                       {!compChargement && plafondAtteint && (
                         <span role="status">{messagePlafond(estHisto ? HISTO_MAX : BARRES_MAX)}</span>
                       )}
                       {compInfo && <span style={{ color: '#B45309' }}>{compInfo}</span>}
                     </>
                   ) : undefined}>
          {selecteurOuvert && (
            <select value="" aria-label="Ajouter une comparaison"
                    disabled={plafondAtteint || !!compChargement}
                    title={plafondAtteint
                      ? messagePlafond(estHisto ? HISTO_MAX : BARRES_MAX) : undefined}
                    onChange={e => (estHisto ? ajouterHisto : ajouterBarre)(e.target.value)}
                    style={STYLE_SELECT_COMPARER}>
              <option value="">+ ajouter…</option>
              {estHisto && (
                <optgroup label="Échelles">
                  {HIST_ECHELLES.filter(c => !dejaAjoute(c.id)).map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </optgroup>
              )}
              {!!token && !!communesDispo?.length && (
                <optgroup label="Communes (témoins et jetons)">
                  {communesDispo.filter(c => !dejaAjoute(c.code)).map(c => (
                    <option key={c.code} value={c.code}>{libelleCommune(c)}</option>
                  ))}
                </optgroup>
              )}
            </select>
          )}
        </ComparerA>
      )}
      {/* Pyramide : la granularité est un réglage de lecture (hors cadre) ; le référent est
          une cible de comparaison (dans le cadre, avec la pastille creuse de son contour). */}
      {vue === 'graphique' && aSwitches && spec.detail && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                      marginBottom: 6, fontSize: 12.5 }}>
          <div role="group" aria-label="Granularité de la pyramide"
               style={{ display: 'inline-flex', border: '1px solid var(--c-border, #E2E8F0)',
                        borderRadius: 6, overflow: 'hidden' }}>
            {([['quinq', 'Tranches de 5 ans'], ['age', 'Âge par âge']] as const).map(([id, lab]) => (
              <button key={id} onClick={() => setGran(id)}
                      aria-pressed={gran === id}
                      style={{ padding: '3px 10px', border: 'none', cursor: 'pointer',
                               background: gran === id ? color : 'transparent',
                               color: gran === id ? '#fff' : 'inherit' }}>{lab}</button>
            ))}
          </div>
        </div>
      )}
      {vue === 'graphique' && aSwitches && spec.comparable && token && (
        <ComparerA principale={{ pastilles: [{ couleur: theme.pyramide[0] }, { couleur: theme.pyramide[1] }] }}
                   puces={cible === 'aucune' ? [] : [{
                     cle: cible, libelle: libelleReferent(cible),
                     pastilles: [{ couleur: theme.contour, contour: true }],
                     onRetirer: () => setCible('aucune'),
                     // Le contour n'est tracé que lorsque le référent est arrivé avec des
                     // tranches : deux séries de plus que la pyramide (H et F). Sinon, la
                     // légende imprimée nommerait une comparaison absente du papier.
                     tracee: effectif.datasets.length > 2,
                   }]}
                   messages={cible === 'aucune' ? undefined : refErreur
                     ? <span style={{ color: '#B45309' }}>{refErreur}</span>
                     : (
                       <span role={refs[clePyr] ? undefined : 'status'}
                             aria-live={refs[clePyr] ? undefined : 'polite'}>
                         {refs[clePyr] ? 'en % de la population de chaque territoire'
                           : 'Chargement de la comparaison…'}
                       </span>
                     )}>
          <select value={cible} onChange={e => setCible(e.target.value as typeof cible)}
                  aria-label="Référent de comparaison de la pyramide"
                  style={STYLE_SELECT_COMPARER}>
            {CIBLES_PYR.map(c => (
              <option key={c.id} value={c.id}>{c.id === 'aucune' ? c.label : libelleReferent(c.id)}</option>
            ))}
            {!!communesDispo?.length && (
              <optgroup label="Communes (témoins et jetons)">
                {communesDispo.map(c => (
                  <option key={c.code} value={`c:${c.code}`}>{libelleCommune(c)}</option>
                ))}
              </optgroup>
            )}
          </select>
        </ComparerA>
      )}
      <div className={['obs2-chart-wrap', haute ? 'haute' : '', specTrace ? 'structure' : '']
                        .filter(Boolean).join(' ')} hidden={vue === 'tableau'}
           style={specTrace ? { '--parts': specTrace.labels.length } as CSSProperties
             : spec.type === 'pyramid' ? { height: gran === 'age' ? 480 : 340 }
             // panneaux : chaque mesure garde la hauteur d'un graphique lisible
             : spec.panneaux ? { height: (haute ? 230 : 170) * Math.max(1, spec.datasets.length) }
             : spec.horizontal ? { height: hauteurHorizontale(effectif.labels.length) } : undefined}>
        <canvas ref={canvasRef} role="img"
          aria-label={`${titre ? `Graphique : ${titre}` : 'Graphique de données territoriales'}${
            centre ? ` — ${centre}` : ''}`} />
      </div>
      {/* A25 : la consigne se pose SOUS le tracé, là où se trouve la légende
          (`position: 'bottom'`), et seulement quand elle a un objet. */}
      {vue === 'graphique' && !isPrint && legendeFiltrable && (
        <p className="obs2-legende-aide">
          Cocher / décocher une entrée de la légende pour filtrer le graphique et son tableau.
        </p>
      )}
      {/* C1 : le tableau, quand le lecteur le demande — filtré par la légende (A11),
          valeurs absolues comprises (A10). Revue codex P2 (PR #722) : il ne reçoit que les
          masques de la forme TRACÉE, la règle même du graphe (parts sur un anneau, séries
          ailleurs) — une part masquée sur l'anneau ne retire jamais une ligne des barres. */}
      {vue === 'tableau' && (
        <TableauSpec spec={tableauTrace} titre={titre}
                     seriesMasquees={tableauTrace.type === 'doughnut' ? undefined : seriesVues}
                     pointsMasques={tableauTrace.type === 'doughnut' ? pointsMasques : undefined} />
      )}
      {/* A27 : sur la route print, le tableau s'ajoute SOUS le graphique, toujours — le PDF
          est une capture Chromium et les étiquettes-valeurs y sont sacrifiées dès que le
          graphique est dense (anti-collision, empilés muets). Le tableau est la seule forme
          déterministe et toujours lisible sur papier (note du 03/09, § 4). */}
      {isPrint && <TableauSpec spec={effectif} titre={titre} compact />}
      {/* Repli des comparaisons (revue codex, 3e passe) : l'impression du navigateur (Ctrl+P)
          d'une tuile repliée garde les comparaisons — le tableau du graphique complet, réservé
          au papier (`.obs2-repli-impression`, repli.css), sous le tracé replié. */}
      {replie && (
        <div className="obs2-repli-impression">
          <TableauSpec spec={effectifComplet} titre={titre} compact
                       seriesMasquees={effectifComplet.type === 'doughnut' ? undefined : seriesMasquees} />
        </div>
      )}
      {vue === 'graphique' && aSwitches && (gran === 'age' || cible !== 'aucune') && (
        <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--c-muted, #64748B)' }}>
          Affichage : {gran === 'age' ? 'âge par âge' : 'tranches de 5 ans'}
          {cible !== 'aucune'
            ? ' — en % de la population de chaque territoire (afin de rendre les tailles comparables)'
            : ''}
        </p>
      )}
      {vue === 'graphique' && estHisto && (histoMode === 'base100' || histoCibles.length > 0) && (
        <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--c-muted, #64748B)' }}>
          {/* « à sa première année affichée », pas « en {année} » (revue codex P2) : une
              série qui commence plus tard — DROM dès 1954/1962 — est rebasée à SA première
              valeur visible ; l'annoncer à l'année choisie aurait menti sur sa croissance. */}
          {histoMode === 'base100'
            ? 'Base 100 pour chaque série à sa première année affichée : on compare des '
              + 'rythmes, pas des tailles — les effectifs restent lisibles au survol.'
            : 'Effectifs réels : un périmètre plus large aplatit la courbe communale — '
              + 'basculez en base 100 pour comparer les rythmes.'}
        </p>
      )}
      {seriesNonIndexables.length > 0 && (
        /* C7 reste : la série absente se DIT. Indexer une série nulle n'a pas de sens
           mathématique (pas de première valeur de référence) — la courbe base 100
           n'existe pas, et c'est une réponse, pas un trou. */
        <p style={{ margin: '4px 0 0', fontSize: 11.5, color: '#B45309' }}>
          {seriesNonIndexables.join(' · ')} : historique entièrement à zéro sur la fenêtre
          affichée — pas de base 100 possible (rien à indexer). Les valeurs réelles, un
          zéro constant, restent lisibles en mode
          {' '}{spec.histo_unite ? `« Valeurs (${spec.histo_unite}) »` : '« Effectifs »'}.
        </p>
      )}
    </div>
  )
}
